import { parseScheduledDate } from '~shared/utils/scheduled-date';
import {
  ComputePortalVisibilityEndDateInput,
  DEFAULT_EVENT_AUTO_HIDE_GRACE_HOURS,
  EVENT_AUTO_HIDE_GRACE_HOUR_VALUES,
  EVENT_TIME_CONFIG,
  EVENT_WEEKDAY_BY_INDEX,
  EventAutoHideGraceHours,
  EventAvailabilitySlot,
} from './events.types';

export function isEventAutoHideGraceHours(
  value: unknown,
): value is EventAutoHideGraceHours {
  return (
    typeof value === 'number' &&
    (EVENT_AUTO_HIDE_GRACE_HOUR_VALUES as readonly number[]).includes(value)
  );
}

function resolveAutoHideGraceHours(value?: number | null): number {
  return isEventAutoHideGraceHours(value)
    ? value
    : DEFAULT_EVENT_AUTO_HIDE_GRACE_HOURS;
}

export function parseEventClockTime(
  time?: string | null,
): { hours: number; minutes: number } | null {
  try {
    if (!time) {
      return null;
    }
    const match = time.trim().match(/^(\d{1,2}):(\d{2})$/);
    if (!match) {
      return null;
    }
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (
      !Number.isInteger(hours) ||
      !Number.isInteger(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      return null;
    }
    return { hours, minutes };
  } catch (error) {
    console.error('Failed to parse event clock time', { time, error });
    throw error;
  }
}

function toDateOrNull(value?: Date | string | null): Date | null {
  if (value == null || value === '') {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const dateOnly = parseScheduledDate(value);
  if (dateOnly) {
    return dateOnly;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date;
}

function resolveLastOccurrenceEndTime(
  input: ComputePortalVisibilityEndDateInput,
  endDate: Date,
): string | undefined {
  if (
    input.timeConfig === EVENT_TIME_CONFIG.DIFFERENT &&
    Array.isArray(input.availability) &&
    input.availability.length > 0
  ) {
    const weekday = EVENT_WEEKDAY_BY_INDEX[endDate.getDay()];
    const weekdaySlot = input.availability.find(
      (slot: EventAvailabilitySlot) => slot.day === weekday && slot.endTime,
    );
    if (weekdaySlot?.endTime) {
      return weekdaySlot.endTime;
    }
    const lastSlot = input.availability[input.availability.length - 1];
    if (lastSlot?.endTime) {
      return lastSlot.endTime;
    }
  }
  return input.endTime ?? undefined;
}

export function computePortalVisibilityEndDate(
  input: ComputePortalVisibilityEndDateInput,
): Date | null {
  try {
    if (input.autoHideAfterEnd === false) {
      return null;
    }
    const endDate = toDateOrNull(input.endDate);
    if (!endDate) {
      return null;
    }
    const hideAt = new Date(endDate);
    const clock = parseEventClockTime(
      resolveLastOccurrenceEndTime(input, endDate),
    );
    if (clock) {
      hideAt.setHours(clock.hours, clock.minutes, 0, 0);
    } else {
      hideAt.setHours(0, 0, 0, 0);
    }
    const graceHours = resolveAutoHideGraceHours(input.autoHideGraceHours);
    hideAt.setTime(hideAt.getTime() + graceHours * 60 * 60 * 1000);
    return hideAt;
  } catch (error) {
    console.error('Failed to compute portal visibility end date', {
      input,
      error,
    });
    throw error;
  }
}

export function buildEventFormAutoHideHint(input: {
  autoHideAfterEnd: boolean;
  autoHideGraceHours: number;
  endDate?: string;
  endTime?: string;
  timeConfig?: string;
  availability?: EventAvailabilitySlot[] | null;
}): string {
  const hideByDate = computePortalVisibilityEndDate({
    autoHideAfterEnd: input.autoHideAfterEnd,
    autoHideGraceHours: input.autoHideGraceHours,
    endDate: input.endDate,
    endTime: input.endTime,
    timeConfig: input.timeConfig,
    availability: input.availability,
  });
  return getEventAutoHideHint({
    autoHideAfterEnd: input.autoHideAfterEnd,
    hideByDate,
    hasEndDate: Boolean(input.endDate),
  });
}

export function getEventAutoHideHint(input: {
  autoHideAfterEnd: boolean;
  hideByDate: Date | null;
  hasEndDate: boolean;
}): string {
  try {
    if (!input.autoHideAfterEnd) {
      return 'This event stays visible until you hide it from the parent portal.';
    }
    if (!input.hasEndDate || !input.hideByDate) {
      return 'Set an end date to auto-hide this event after it ends.';
    }
    return `Will hide from portal on ${input.hideByDate.toLocaleString()}.`;
  } catch (error) {
    console.error('Failed to build event auto-hide hint', { input, error });
    throw error;
  }
}
